import { useState, type ReactElement } from "react";
import { IconChevronDown } from "@tabler/icons-react";
import type { PropertySummary } from "@shared/api";
import type { BuildabilityScore } from "../../score/computeBuildabilityScore";
import type { InspectorPresentation } from "./BaseLayersCard";
import { ProgressRow } from "./ProgressRow";
import { SubsectionHeader } from "./SubsectionHeader";
import styles from "./PropertyReportMetricsCard.module.css";

function fillWidthPercent(
  contribution: number,
  weight: number
): number {
  if (weight <= 0) return 0;
  return Math.min(100, Math.max(0, (contribution / weight) * 100));
}

/**
 * Lifecycle states for the AI buyer paragraph block. `idle` means
 * "no fetch has been kicked off" (free-tier viewer, or pre-fetch state)
 * and renders nothing — the paragraph block is paid-tier-only and
 * shouldn't take vertical space when it isn't expected to appear.
 */
export type PropertySummaryStatus =
  | "idle"
  | "loading"
  | "ready"
  | "unavailable";

export interface PropertyReportMetricsCardProps {
  buildabilityScore: BuildabilityScore | null;
  /** AI buyer paragraph; null when not yet generated or unavailable. */
  summary: PropertySummary | null;
  /** Lifecycle state controlling skeleton/paragraph/error rendering. */
  summaryStatus: PropertySummaryStatus;
  presentation?: InspectorPresentation;
}

/**
 * Buildability score body: hero numeral + band, one progress row per
 * component, and the AI summary tucked behind a "Read summary" disclosure
 * so the metric bars are what you see first.
 */
export function PropertyReportMetricsCard({
  buildabilityScore,
  summary,
  summaryStatus,
  presentation = "inspector"
}: PropertyReportMetricsCardProps): ReactElement {
  const drawerRail = presentation === "drawer";

  if (buildabilityScore == null) {
    return (
      <p className={styles.empty}>
        Score compiles after lot analysis and flood/wetlands context finish
        loading.
      </p>
    );
  }

  return (
    <div>
      <div className={styles.scoreRow}>
        <div className={styles.scoreLeft}>
          <p className={styles.scoreNumber}>{buildabilityScore.total}</p>
          <p className={styles.scoreSuffix}>/ 100</p>
        </div>
        <p className={styles.band}>{buildabilityScore.band}</p>
      </div>
      <SubsectionHeader label="Score breakdown" />
      {buildabilityScore.components.map((c) => (
        <ProgressRow
          key={c.key}
          label={c.label}
          fillPercent={fillWidthPercent(c.contribution, c.weight)}
          sideText={`${c.contribution.toFixed(0)}/${c.weight}`}
          title={c.detail}
          drawerRail={drawerRail}
        />
      ))}
      <SummaryDisclosure summary={summary} status={summaryStatus} />
    </div>
  );
}

/**
 * AI summary beneath the metric bars. Returns null for `idle` so free-tier
 * viewers don't see an empty placeholder; otherwise a collapsed "Read
 * summary" row that expands into the skeleton / paragraph / fallback line.
 */
function SummaryDisclosure({
  summary,
  status
}: {
  summary: PropertySummary | null;
  status: PropertySummaryStatus;
}): ReactElement | null {
  const [open, setOpen] = useState(false);
  if (status === "idle") return null;
  if (status === "ready" && !summary) return null;

  // `model: "demo"` marks the hand-written Crow Hill demo paragraph (see
  // src/data/demoPropertySummary.ts) — disclose it as sample content so it
  // isn't mistaken for live AI output for this parcel.
  const isDemoSummary = summary?.model === "demo";

  let body: ReactElement;
  if (status === "loading") {
    body = (
      <>
        <div
          className={styles.summarySkeleton}
          aria-busy="true"
          aria-live="polite"
        >
          <div className={styles.summarySkeletonLine} />
          <div className={styles.summarySkeletonLine} />
          <div className={styles.summarySkeletonLine} />
          <div className={styles.summarySkeletonLine} />
        </div>
        <p className={styles.summaryStatusLine}>Generating property summary…</p>
      </>
    );
  } else if (status === "unavailable") {
    body = (
      <p className={styles.summaryUnavailable}>
        Property summary is unavailable for this report.
      </p>
    );
  } else {
    body = (
      <>
        {isDemoSummary ? (
          <p className={styles.summaryDemoBadgeRow}>
            <span className={styles.summaryDemoBadge}>Sample summary</span>
          </p>
        ) : null}
        <p className={styles.summaryText}>{summary!.text}</p>
      </>
    );
  }

  return (
    <div className={styles.summary}>
      <button
        type="button"
        className={styles.summaryToggle}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className={styles.summaryToggleLabel}>
          {status === "loading" ? "Summary (generating…)" : "Read summary"}
        </span>
        <IconChevronDown
          size={16}
          stroke={1.5}
          className={`${styles.summaryChevron}${open ? ` ${styles.summaryChevronOpen}` : ""}`}
        />
      </button>
      {open ? <div className={styles.summaryBody}>{body}</div> : null}
    </div>
  );
}
